import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import { Checkbox } from './Checkbox.js';

export interface ListItemProps {
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  title: string;
  subtitle?: string;
  subtitleLines?: 1 | 2;
  meta?: ReactNode;
  badges?: ReactNode[];
  unread?: boolean;
  active?: boolean;
  onClick?: () => void;
  actions?: ReactNode;
  alwaysShowActions?: boolean;
  density?: 'compact' | 'default' | 'comfortable';
  itemId?: string | number;
}

type DensityPad = { v: string; h: string };
const PADS: Record<NonNullable<ListItemProps['density']>, DensityPad> = {
  compact:     { v: '8px',  h: '10px' },
  default:     { v: '14px', h: 'var(--evo-list-pad-x)' },
  comfortable: { v: '14px', h: '24px' },
};

export function ListItem({
  selected,
  onSelect,
  title,
  subtitle,
  subtitleLines = 1,
  meta,
  badges,
  unread,
  active,
  onClick,
  actions,
  alwaysShowActions = false,
  density = 'default',
  itemId,
}: ListItemProps) {
  const [hovered, setHovered] = useState(false);
  const { v, h } = PADS[density];

  const filteredBadges = badges?.filter(Boolean) ?? [];
  const hasBadges = filteredBadges.length > 0;
  const hasMeta = meta != null;
  const showHoverActions = !alwaysShowActions && !!actions && hovered;
  const hasRightCol = hasMeta || showHoverActions || hasBadges;

  return (
    <div
      className="evo-list-item"
      data-list-item-id={itemId}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', flexDirection: 'column', cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Content row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          minHeight: 'var(--evo-list-row-min-height)',
          boxShadow: active ? 'inset 2px 0 0 0 var(--evo-cyan)' : 'none',
          background: active
            ? 'rgba(0,250,200,0.06)'
            : hovered
            ? 'var(--evo-bg-3)'
            : 'transparent',
          transition: 'background 80ms',
        }}
      >
        {/* Checkbox zone */}
        {onSelect && (
          <div
            onClick={e => e.stopPropagation()}
            style={{ alignSelf: 'flex-start', display: 'flex', padding: `${v} var(--evo-list-checkbox-gap) 0 ${h}`, flexShrink: 0 }}
          >
            <div style={{ marginTop: 2 }}>
              <Checkbox checked={!!selected} onChange={onSelect} />
            </div>
          </div>
        )}

        {/* Content area — left inset comes from the checkbox zone's own
            padding when onSelect is present (default h/left is 0 here to
            avoid doubling it); when there's no checkbox (e.g. a non-bulk-
            selectable list like Billing's invoices view), this provides the
            same var(--evo-list-pad-x) inset directly, matching the always-
            present separator below and every selectable row. */}
        <div style={{ flex: 1, minWidth: 0, padding: `${v} 0 ${v} ${onSelect ? '0' : h}`, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              title={title}
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                fontSize: 'var(--evo-list-title-fs)',
                fontWeight: unread ? 600 : 400,
                color: active ? 'var(--evo-cyan)' : 'var(--evo-fg-1)',
                fontFamily: 'var(--evo-font)',
              }}
            >
              {title}
            </span>
            {unread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--evo-cyan)', flexShrink: 0 }} />}
          </div>

          {subtitle != null && (
            <span
              style={{
                display: subtitleLines === 2 ? '-webkit-box' : 'block',
                ...(subtitleLines === 2
                  ? { WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }
                  : { whiteSpace: 'nowrap' }),
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: 'var(--evo-list-subtitle-fs)',
                color: 'var(--evo-fg-3)',
                fontFamily: 'var(--evo-font)',
              } as CSSProperties}
            >
              {subtitle}
            </span>
          )}

          {/* Inline actions — contact-style alwaysShowActions */}
          {alwaysShowActions && actions && (
            <div
              onClick={e => e.stopPropagation()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}
            >
              {actions}
            </div>
          )}
        </div>

        {/* Right column: meta (top) + badges (bottom) */}
        {hasRightCol && (
          <div
            style={{
              flexShrink: 0,
              minWidth: 80,
              maxWidth: 200,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              padding: `${v} ${h} ${v} 4px`,
            }}
          >
            {hasMeta && (
              <span style={{ fontSize: 'var(--evo-list-meta-fs)', color: 'var(--evo-fg-3)', whiteSpace: 'nowrap' }}>
                {meta}
              </span>
            )}
            {showHoverActions && (
              <div onClick={e => e.stopPropagation()} style={{ marginTop: 'auto' }}>
                {actions}
              </div>
            )}
            {hasBadges && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  gap: 4,
                  justifyContent: 'flex-end',
                  marginTop: 'auto',
                }}
              >
                {filteredBadges}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Inset separator */}
      <div style={{ margin: '0 var(--evo-list-pad-x)', height: '1px', background: 'var(--evo-stroke)', flexShrink: 0 }} />
    </div>
  );
}
